import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { Content } from "ionic-angular";
import CalendarEvent from "models/calendar/calendar-event";
import {
  TaskDetailsCount,
  TaskDetailsFilter,
  TaskDetailsQaReturned,
  TasksCount,
} from "models/dashboard/task-details-filter";
import * as moment from "moment-timezone";
import { NavigationService } from "navigation/navigation.service";
import { StateManagementService } from "providers/state-management/state-management.service";
import { Subscription } from "rxjs";
import { combineLatest } from "rxjs/observable/combineLatest";
import { debounceTime, distinctUntilChanged, take } from "rxjs/operators";
import { IUser } from "../../../../app/auth/user.model";
import { QaQueueComments } from "../../../../components/qa-control/models/qa-queue-comments";
import { CalendarService } from "../../../../providers/calendar-service/calendar-service";
import { QaQueueService } from "../../../../providers/qa-queue/qa-queue.service";
import { UtilityService } from "../../../../providers/utility-service/utility-service";

@Component({
  selector: "task-details-table",
  templateUrl: "task-details-table.html",
})
export class TaskDetailsTableComponent implements OnInit, OnDestroy {
  currentPage: number = 0;
  totalPageCount: number;
  taskDetails: CalendarEvent[] = [];
  taskDetailsCount: TaskDetailsCount;
  taskDetailsQaReturned: TaskDetailsQaReturned = {
    last60QAReturned: false,
    yesterdayQAReturned: false,
    todayQAReturned: false,
    tomorrowQAReturned: false,
    last7QAReturned: false,
  };
  commentCategories: any;
  visitsComments: QaQueueComments;
  sortColumn: string;
  sortDirection: string;
  private contentPlaceholder: Content;
  hasScrollbar: boolean = false;
  taskDetailsSubscription: Subscription[] = [];
  fetchingTasks: boolean;
  private showTaskDetails: boolean; // combine either PatientCalendar or QaQueue Toggle
  private isPatientCalendarToggleOn: boolean;
  private qaQueueToggle: boolean;
  taskDetailsFilter: TaskDetailsFilter = TaskDetailsFilter.TODAY;
  @Input() user: IUser | undefined;
  lastFrom: number = 0;
  lastRecords = {
    currentFrom: null,
    totalHits: 0,
  };
  pageSize: number = 20;
  filters: TaskDetailsFilter[] = [
    TaskDetailsFilter.LAST_60_DAYS,
    TaskDetailsFilter.LAST_7_DAYS,
    TaskDetailsFilter.YESTERDAY,
    TaskDetailsFilter.TODAY,
    TaskDetailsFilter.TOMORROW,
  ];
  sortTasksColumn: string;
  sortTasksDirection: string;

  taskDateRanges: { [key: string]: { start: string; end: string } } = {};
  public dateDisplay: { [key: string]: string } = {};
  @ViewChild("filterContainer") filterContainer: ElementRef;
  startIndex = 2;
  itemsPerPage = 5;
  showFilterArrows: boolean = true;

  @ViewChild("contentPlaceholder") set content(content: Content) {
    if (content) {
      this.contentPlaceholder = content;
      this.contentPlaceholder.resize();
      this.checkForScrollbar();
    }
  }

  @HostListener("window:resize", ["$event"])
  onResize() {
    this.checkForScrollbar();
    this.updateFilterItems();
  }

  constructor(
    private navSvc: NavigationService,
    private stateData: StateManagementService,
    private calendarService: CalendarService,
    private utilityService: UtilityService,
    private qaQueueService: QaQueueService
  ) {}

  initTaskDetailsToggles(): void {
    this.taskDetailsSubscription.push(
      combineLatest(
        this.stateData.isPatientCalendarToggleOn,
        this.stateData.qaQueueToggle
      )
        .pipe(debounceTime(250), distinctUntilChanged(), take(3))
        .subscribe(([isPatientCalendarToggleOn, qaQueueToggle]) => {
          this.showTaskDetails = isPatientCalendarToggleOn || qaQueueToggle;
          this.isPatientCalendarToggleOn = isPatientCalendarToggleOn;
          this.qaQueueToggle = qaQueueToggle;
          if (this.showTaskDetails) {
            this.calculateDateRanges();
            this.computeDateDisplay();
            this.setDefaultSorting();
            this.initTaskDetailsCount();
            this.makeTaskDetails();
            if (this.qaQueueToggle) this.makeTaskDetails(true);
            if (this.taskDetailsCount)
              this.getTaskCount(this.taskDetailsFilter);
          }
        })
    );
  }

  ngOnInit(): void {
    this.initTaskDetailsToggles();
    this.updateFilterItems();
  }

  ngOnDestroy(): void {
    this.taskDetailsSubscription.forEach((s) => s.unsubscribe());
  }

  sortBy(selectedCol) {
    this.taskDetails = [];
    this.currentPage = 0;
    if (this.sortColumn === selectedCol) {
      this.sortDirection = this.sortDirection === "ASC" ? "DESC" : "ASC";
    } else {
      this.sortColumn = selectedCol;
      this.sortDirection = "ASC";
    }

    this.sortTasksColumn = this.sortColumn;
    this.sortTasksDirection = this.sortDirection;
    this.makeTaskDetails();
  }

  setDefaultSorting() {
    this.sortTasksColumn = this.sortColumn = "startDatetime";
    this.sortTasksDirection = this.sortDirection = "DESC";
  }

  filterBy(filter: TaskDetailsFilter) {
    this.setDefaultSorting();
    this.currentPage = 0;
    this.taskDetails = [];
    this.taskDetailsFilter = filter;
    this.makeTaskDetails();
  }

  redirectPage(page) {
    if (page === "CalendarPage" && this.showTaskDetails) {
      this.navSvc.goToInternalPage("NewCalendarPage", { patChartId: 0 });
    } else if (page === "CalendarPage") {
      this.navSvc.goToInternalPage("CalendarPage", { patChartId: 0 });
    } else {
      this.navSvc.goToInternalPage(page, {});
    }
  }

  hasCalendarEvents(): boolean {
    return (
      typeof this.taskDetails != "undefined" && this.taskDetails.length > 0
    );
  }

  handleTaskFilter() {
    this.currentPage = 0;
    this.taskDetails = [];
    this.makeTaskDetails();
  }

  eventUpdated() {
    this.initTaskDetailsCount();
    this.handleTaskFilter();
  }

  getTaskCount(filter: TaskDetailsFilter): number {
    let taskCount = 0;
    switch (filter) {
      case TaskDetailsFilter.LAST_60_DAYS:
        taskCount = this.taskDetailsCount.last60Count;
        break;
      case TaskDetailsFilter.LAST_7_DAYS:
        taskCount = this.taskDetailsCount.last7Count;
        break;
      case TaskDetailsFilter.YESTERDAY:
        taskCount = this.taskDetailsCount.yesterdayCount;
        break;
      case TaskDetailsFilter.TODAY:
        taskCount = this.taskDetailsCount.todayCount;
        break;
      case TaskDetailsFilter.TOMORROW:
        taskCount = this.taskDetailsCount.tomorrowCount;
        break;
    }
    return taskCount;
  }

  getQAReturned(filter: TaskDetailsFilter): boolean {
    switch (filter) {
      case TaskDetailsFilter.LAST_60_DAYS:
        return this.taskDetailsQaReturned.last60QAReturned !== undefined
          ? this.taskDetailsQaReturned.last60QAReturned
          : false;
      case TaskDetailsFilter.LAST_7_DAYS:
        return this.taskDetailsQaReturned.last7QAReturned !== undefined
          ? this.taskDetailsQaReturned.last7QAReturned
          : false;
      case TaskDetailsFilter.YESTERDAY:
        return this.taskDetailsQaReturned.yesterdayQAReturned !== undefined
          ? this.taskDetailsQaReturned.yesterdayQAReturned
          : false;
      case TaskDetailsFilter.TODAY:
        return this.taskDetailsQaReturned.todayQAReturned !== undefined
          ? this.taskDetailsQaReturned.todayQAReturned
          : false;
      case TaskDetailsFilter.TOMORROW:
        return this.taskDetailsQaReturned.tomorrowQAReturned !== undefined
          ? this.taskDetailsQaReturned.tomorrowQAReturned
          : false;
      default:
        return false;
    }
  }

  calculateDateRanges() {
    const today = new Date();

    const last60Start = this.formatDate(this.addDays(today, -68));
    const last60End = this.formatDate(this.addDays(today, -9));

    const last7Start = this.formatDate(this.addDays(today, -8));
    const last7End = this.formatDate(this.addDays(today, -2));

    const yesterday = this.formatDate(this.addDays(today, -1));

    const todayFormatted = this.formatDate(today);

    const tomorrow = this.formatDate(this.addDays(today, 1));

    this.taskDateRanges[TaskDetailsFilter.LAST_60_DAYS] = {
      start: last60Start,
      end: last60End,
    };
    this.taskDateRanges[TaskDetailsFilter.LAST_7_DAYS] = {
      start: last7Start,
      end: last7End,
    };
    this.taskDateRanges[TaskDetailsFilter.YESTERDAY] = {
      start: yesterday,
      end: yesterday,
    };
    this.taskDateRanges[TaskDetailsFilter.TODAY] = {
      start: todayFormatted,
      end: todayFormatted,
    };
    this.taskDateRanges[TaskDetailsFilter.TOMORROW] = {
      start: tomorrow,
      end: tomorrow,
    };
  }

  addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  formatDate(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  computeDateDisplay() {
    this.filters.forEach((filter) => {
      const range = this.taskDateRanges[filter];
      if (range.start === range.end) {
        this.dateDisplay[filter] = `(${range.start})`;
      } else {
        this.dateDisplay[filter] = `(${range.start}-${range.end})`;
      }
    });
  }

  searchSchedulingEvents(request: any, showQAReturned?: boolean) {
    request.size = showQAReturned ? 200 : this.pageSize;
    if (!request) {
      return;
    }
    this.taskDetailsSubscription.push(
      this.calendarService.getSchedulingEvents(request).subscribe(
        (response) => {
          if (showQAReturned && response["hits"]) {
            this.setQAReturnedIndicator(response["hits"]);
          } else {
            if (this.taskDetailsCount) {
              this.setTaskDetailsCount(this.taskDetailsFilter, response);
            }

            if (response && response["hits"] && response["totalHits"] > 0) {
              this.taskDetails.push(
                ...this.calendarService.formatSchedulingResponse(
                  response["hits"]
                )
              );
              const visitsIds = this.taskDetails
                .filter((task) => task.status === "QA RETURNED")
                .map((task) => task.visitId.toString());
              if (visitsIds.length > 0) {
                this.getVisitsComments(visitsIds);
              }
              this.lastFrom = this.currentPage * this.pageSize;
              this.lastRecords.currentFrom = request.from;
              this.lastRecords.totalHits = response["totalHits"];
              this.currentPage++;
            } else if (
              (response && !response["hits"]) ||
              response["totalHits"] == 0
            ) {
              this.lastRecords.currentFrom = request.from;
              this.lastRecords.totalHits = 0;
            }
          }
          this.fetchingTasks = false;
          this.totalPageCount = Math.ceil(
            this.taskDetails.length / this.pageSize
          );
          this.utilityService.decreaseSpinnerCount();
        },
        (err) => {
          this.utilityService.decreaseSpinnerCount();
          this.calendarService.presentErrorToast(
            err,
            "Error loading calendar events"
          );
        }
      )
    );
  }

  makeTaskDetails(showQAReturned?: boolean) {
    if (this.user.userId) {
      let requestBody = this.createSearchRequestBody(showQAReturned);
      this.fetchingTasks = true;
      this.searchSchedulingEvents(requestBody, showQAReturned);
    }
  }

  setTaskDetailsCount(taskDetailsFilter: string, hits: any) {
    switch (taskDetailsFilter) {
      case TaskDetailsFilter.TOMORROW:
        this.taskDetailsCount.tomorrowCount = hits["totalHits"];
        break;
      case TaskDetailsFilter.TODAY:
        this.taskDetailsCount.todayCount = hits["totalHits"];
        break;
      case TaskDetailsFilter.YESTERDAY:
        this.taskDetailsCount.yesterdayCount = hits["totalHits"];
        break;
      case TaskDetailsFilter.LAST_7_DAYS:
        this.taskDetailsCount.last7Count = hits["totalHits"];
        break;
      case TaskDetailsFilter.LAST_60_DAYS:
        this.taskDetailsCount.last60Count = hits["totalHits"];
        break;
    }
  }

  setQAReturnedIndicator(hits: any) {
    const guestTimeZone = moment.tz.guess();
    const start60Days = moment
      .tz(guestTimeZone)
      .subtract(68, "days")
      .startOf("day");
    const end60Days = moment.tz(guestTimeZone).subtract(9, "days").endOf("day");

    const start7Days = moment
      .tz(guestTimeZone)
      .subtract(8, "days")
      .startOf("day");
    const end7Days = moment.tz(guestTimeZone).subtract(2, "days").endOf("day");

    hits.forEach((task) => {
      if (task.status === "QA RETURNED") {
        const taskDate = moment(task.startDatetime);
        if (taskDate.isBetween(start60Days, end60Days)) {
          this.taskDetailsQaReturned.last60QAReturned = true;
        }
        if (taskDate.isBetween(start7Days, end7Days)) {
          this.taskDetailsQaReturned.last7QAReturned = true;
        }
        if (taskDate.isSame(moment().subtract(1, "days"), "day")) {
          this.taskDetailsQaReturned.yesterdayQAReturned = true;
        }
        if (taskDate.isSame(moment(), "day")) {
          this.taskDetailsQaReturned.todayQAReturned = true;
        }
        if (taskDate.isSame(moment().add(1, "days"), "day")) {
          this.taskDetailsQaReturned.tomorrowQAReturned = true;
        }
      }
    });
  }

  initTaskDetailsCount() {
    let requestBody: any = this.createTaskCountsBody();
    this.taskDetailsSubscription.push(
      this.calendarService.getSchedulingCounts(requestBody).subscribe(
        (response) => {
          if (response && response.length > 0) {
            this.setCalendarCountResponse(response);
          }
        },
        (err) => {
          this.calendarService.presentErrorToast(
            err,
            "Error loading calendar counts"
          );
        }
      )
    );
  }

  setCalendarCountResponse(response: any) {
    this.taskDetailsCount = {} as TaskDetailsCount;
    response.forEach((count: TasksCount) => {
      this.setTaskDetailsCount(count.countOf, count);
    });
  }

  createSearchRequestBody(showQAReturned?: boolean) {
    let details: any = this.getMustAndOptionalFields(showQAReturned);

    const guestTimeZone = moment.tz.guess();
    let startInGuestTZ = moment.tz(guestTimeZone).startOf("day");
    let endInGuestTZ = startInGuestTZ;

    let startOfDayUTC: any;
    let endOfDayUTC: any;

    if (showQAReturned) {
      startInGuestTZ = moment
        .tz(guestTimeZone)
        .subtract(68, "days")
        .startOf("day");
      endInGuestTZ = moment.tz(guestTimeZone).add(1, "days").startOf("day");
    } else {
      switch (this.taskDetailsFilter) {
        case TaskDetailsFilter.LAST_60_DAYS:
          startInGuestTZ = moment
            .tz(guestTimeZone)
            .subtract(68, "days")
            .startOf("day");
          endInGuestTZ = moment
            .tz(guestTimeZone)
            .subtract(9, "days")
            .startOf("day");
          break;
        case TaskDetailsFilter.LAST_7_DAYS:
          startInGuestTZ = moment
            .tz(guestTimeZone)
            .subtract(8, "days")
            .startOf("day");
          endInGuestTZ = moment
            .tz(guestTimeZone)
            .subtract(2, "days")
            .startOf("day");
          break;
        case TaskDetailsFilter.YESTERDAY:
          startInGuestTZ = moment
            .tz(guestTimeZone)
            .subtract(1, "days")
            .startOf("day");
          endInGuestTZ = startInGuestTZ;
          break;
        case TaskDetailsFilter.TODAY:
          startInGuestTZ = moment.tz(guestTimeZone).startOf("day");
          endInGuestTZ = startInGuestTZ;
          break;
        case TaskDetailsFilter.TOMORROW:
          startInGuestTZ = moment
            .tz(guestTimeZone)
            .add(1, "days")
            .startOf("day");
          endInGuestTZ = startInGuestTZ;
          break;
        default:
          console.error(
            `Unexpected TaskDetailsFilter value: ${this.taskDetailsFilter}`
          );
          break;
      }
    }
    startOfDayUTC = this.getStartOfDayUTC(startInGuestTZ);
    endOfDayUTC = this.getEndOfDayUTC(endInGuestTZ);

    details.startDateRanges = [
      { name: "startDatetime", fromDate: startOfDayUTC, toDate: endOfDayUTC },
    ];
    // details.endDateRanges = [{name: "endDatetime", fromDate: startOfDayUTC, toDate: endOfDayUTC}];

    details.sortBy = {
      name: this.sortTasksColumn,
      value: this.sortTasksDirection,
    };

    return details;
  }

  createTaskCountsBody(): any {
    const guestTimeZone = moment.tz.guess();

    const createDateRange = (start, end) => ({
      startDateRanges: [
        { name: "startDatetime", fromDate: start, toDate: end },
      ],
      // endDateRanges: [{name: "endDatetime", fromDate: start, toDate: end}]
    });

    const createTaskCount = (label, start, end) => ({
      ...this.getMustAndOptionalFields(),
      ...createDateRange(start, end),
      countOf: TaskDetailsFilter[label],
    });

    const startOfDay = (daysOffset) =>
      moment.tz(guestTimeZone).add(daysOffset, "days").startOf("day");
    const endOfDay = (daysOffset) => startOfDay(daysOffset);

    const startTomwDayUTC = this.getStartOfDayUTC(startOfDay(1));
    const endTomwDayUTC = this.getEndOfDayUTC(endOfDay(1));
    const startTodayDayUTC = this.getStartOfDayUTC(startOfDay(0));
    const endTodayDayUTC = this.getEndOfDayUTC(endOfDay(0));
    const startYesterdayDayUTC = this.getStartOfDayUTC(startOfDay(-1));
    const endYesterdayDayUTC = this.getEndOfDayUTC(endOfDay(-1));
    const start7DayUTC = this.getStartOfDayUTC(startOfDay(-8));
    const end7DayUTC = this.getEndOfDayUTC(endOfDay(-2));
    const start60DayUTC = this.getStartOfDayUTC(startOfDay(-68));
    const end60DayUTC = this.getEndOfDayUTC(endOfDay(-9));

    const body = {
      dataCount: [
        createTaskCount("TOMORROW", startTomwDayUTC, endTomwDayUTC),
        createTaskCount("TODAY", startTodayDayUTC, endTodayDayUTC),
        createTaskCount("YESTERDAY", startYesterdayDayUTC, endYesterdayDayUTC),
        createTaskCount("LAST_7_DAYS", start7DayUTC, end7DayUTC),
        createTaskCount("LAST_60_DAYS", start60DayUTC, end60DayUTC),
      ],
    };

    return body;
  }

  private getStartOfDayUTC(todayInGuestTZ: moment.Moment) {
    return todayInGuestTZ.clone().tz("UTC").format(); // Start of day UTC
  }

  private getEndOfDayUTC(todayInGuestTZ: moment.Moment) {
    return todayInGuestTZ.clone().endOf("day").tz("UTC").format(); // End of day UTC
  }

  getVisitsComments(visitsIds: string[]) {
    this.taskDetailsSubscription.push(
      this.qaQueueService
        .getCommentCategories(this.user.tenantId)
        .subscribe((categories) => {
          this.commentCategories = categories;
        })
    );
    this.taskDetailsSubscription.push(
      this.qaQueueService
        .getActionLatestComments(this.user.tenantId, "VISIT", visitsIds)
        .subscribe((data) => {
          this.visitsComments = data;
        })
    );
  }

  getMustAndOptionalFields(showQAReturned?: boolean): any {
    const baseFields = {
      dataType: "EVENT",
      from: this.currentPage * this.pageSize,
      size: this.pageSize,
      mustFields: [
        { name: "userId", value: this.user.userId.toString() },
        { name: "eventType", value: "VISIT" },
      ],
    };
    const calendarFields = [
      { name: "status", value: "SCHEDULED" },
      { name: "status", value: "STARTED" },
      { name: "status", value: "MISSED" },
    ];
    const qaFields = [
      { name: "status", value: "SUBMITTED TO QA" },
      { name: "status", value: "QA RETURNED" },
    ];

    const qaReturnedFieldOnly = [{ name: "status", value: "QA RETURNED" }];

    if (showQAReturned) {
      return {
        ...baseFields,
        optionalFields: qaReturnedFieldOnly,
      };
    } else if (this.isPatientCalendarToggleOn && this.qaQueueToggle) {
      return {
        ...baseFields,
        optionalFields: [...calendarFields, ...qaFields],
      };
    } else if (this.isPatientCalendarToggleOn) {
      return {
        ...baseFields,
        optionalFields: calendarFields,
      };
    } else if (this.qaQueueToggle) {
      return {
        ...baseFields,
        optionalFields: qaFields,
      };
    }
  }

  doInfinite(infiniteScroll) {
    this.makeTaskDetails();
    setTimeout(() => {
      infiniteScroll.complete();
      infiniteScroll.enable(
        this.taskDetails.length < this.lastRecords.totalHits
      );
    }, 500);
  }

  checkForScrollbar() {
    if (this.contentPlaceholder) {
      const scrollElement = this.contentPlaceholder.getScrollElement();
      this.hasScrollbar =
        scrollElement.scrollHeight > scrollElement.clientHeight;
      if (
        !this.hasScrollbar &&
        this.taskDetails.length < this.lastRecords.totalHits
      ) {
        this.makeTaskDetails();
      }
    }
  }

  get endIndex() {
    return this.startIndex + this.itemsPerPage;
  }

  visibleFilters() {
    return this.filters.slice(this.startIndex, this.endIndex);
  }

  nextPage() {
    if (this.endIndex < this.filters.length) {
      this.startIndex++;
    }
  }

  prevPage() {
    if (this.startIndex > 0) {
      this.startIndex--;
    }
  }

  updateFilterItems() {
    const width = this.filterContainer.nativeElement.offsetWidth;
    this.showFilterArrows = width < 924;
    this.itemsPerPage = width > 924 ? 5 : width > 620 ? 3 : 2;
    this.startIndex = width > 924 ? 0 : width > 620 ? 2 : 3;
  }
}



// WEBPACK FOOTER //
// ./src/pages/common/dashboard/task-details-table/task-details-table.ts